import { useState, useRef, useEffect, useCallback } from 'react';
import type { TaskResource, TaskResourceEmail, TaskResourceText } from '@/types/task';
import { useUpdateTaskResources } from '@/hooks/useTasks';
import CopyButton from '@/components/common/CopyButton';
import Button from '@/components/common/Button';
import Card from '@/components/common/Card';

interface EditableEmailResourceProps {
  resource: TaskResourceEmail;
  resourceIndex: number;
  taskId: string;
  allResources: TaskResource[];
  /** When true, renders without Card wrapper and header (used when embedded in ContentDraftItem) */
  embedded?: boolean;
}

interface EditableTextResourceProps {
  resource: TaskResourceText;
  resourceIndex: number;
  taskId: string;
  allResources: TaskResource[];
  /** When true, renders without Card wrapper and header (used when embedded in ContentDraftItem) */
  embedded?: boolean;
}

/**
 * Auto-resizing textarea that grows with content.
 * When `showFakeCursor` is true, displays a blinking cursor animation at the
 * end of the text until the user focuses the textarea.
 */
function AutoResizeTextarea({
  value,
  onChange,
  className = '',
  placeholder,
  showFakeCursor = false,
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  showFakeCursor?: boolean;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [value]);

  const handleFocus = () => {
    setIsFocused(true);
    // Place cursor at end when user focuses
    const textarea = textareaRef.current;
    if (textarea) {
      const len = textarea.value.length;
      textarea.setSelectionRange(len, len);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={`w-full resize-none overflow-hidden border-0 bg-transparent p-0 focus:outline-none focus:ring-0 ${className}`}
        placeholder={placeholder}
        rows={1}
      />
      {showFakeCursor && !isFocused && <FakeCursor />}
    </div>
  );
}

/**
 * A blinking cursor element positioned after the textarea content.
 * Uses CSS animation to blink, signaling that the text is editable.
 */
function FakeCursor() {
  return (
    <span
      className="inline-block h-[1.2em] w-[1px] bg-primary-600 animate-blink align-text-bottom"
      aria-hidden="true"
    />
  );
}

/**
 * Save button with status feedback (saving, saved, error).
 * Uses onMouseDown with preventDefault to avoid stealing focus from the
 * textarea, which would cause layout shifts and require a double-click.
 */
function SaveButton({
  isDirty,
  isSaving,
  saveError,
  onSave,
}: {
  isDirty: boolean;
  isSaving: boolean;
  saveError: boolean;
  onSave: () => void;
}) {
  const [showSaved, setShowSaved] = useState(false);

  // Show "Saved" confirmation briefly after successful save
  useEffect(() => {
    if (!isSaving && !saveError && showSaved) {
      const timer = setTimeout(() => setShowSaved(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isSaving, saveError, showSaved]);

  // Prevent mousedown from blurring the textarea, which would cause
  // the fake cursor to reappear and shift layout before the click lands.
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  if (showSaved && !isDirty) {
    return (
      <button
        disabled
        className="inline-flex items-center gap-1.5 rounded-lg border border-success-500 px-3 py-1.5 text-sm font-medium text-success-600"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        Saved
      </button>
    );
  }

  return (
    <Button
      variant="primary"
      size="sm"
      disabled={!isDirty || isSaving}
      loading={isSaving}
      onMouseDown={handleMouseDown}
      onClick={() => {
        onSave();
        setShowSaved(true);
      }}
    >
      {saveError ? 'Retry Save' : 'Save'}
    </Button>
  );
}

/**
 * Editable email resource with inline editing for subject and body.
 * The address field remains read-only.
 *
 * When `embedded` is true, renders without the Card wrapper and header
 * (label/icon), since those are provided by the parent ContentDraftItem.
 */
export function EditableEmailResource({
  resource,
  resourceIndex,
  taskId,
  allResources,
  embedded = false,
}: EditableEmailResourceProps) {
  const [subject, setSubject] = useState(resource.subject);
  const [body, setBody] = useState(resource.body);
  const [saveError, setSaveError] = useState(false);

  const updateResources = useUpdateTaskResources();

  const isDirty = subject !== resource.subject || body !== resource.body;

  const handleSave = useCallback(() => {
    setSaveError(false);
    const updatedResources = allResources.map((r, i) => {
      if (i === resourceIndex) {
        return { ...r, subject, body } as TaskResource;
      }
      return r;
    });

    updateResources.mutate(
      { taskId, data: { resources: updatedResources } },
      {
        onError: () => setSaveError(true),
      },
    );
  }, [taskId, resourceIndex, allResources, subject, body, updateResources]);

  // Sync local state if the resource prop changes (e.g., after server refetch)
  useEffect(() => {
    if (!isDirty) {
      setSubject(resource.subject);
      setBody(resource.body);
    }
  }, [resource.subject, resource.body]);

  const fullText = `To: ${resource.address}\nSubject: ${subject}\n\n${body}`;

  const content = (
    <div className="space-y-2">
      {!embedded && (
        <div className="flex items-center gap-2">
          <svg className="h-5 w-5 flex-shrink-0 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="text-sm font-medium text-neutral-900">Email Draft</span>
          {isDirty && (
            <span className="text-xs text-neutral-500 italic">Unsaved changes</span>
          )}
        </div>
      )}
      {embedded && isDirty && (
        <span className="text-xs text-neutral-500 italic">Unsaved changes</span>
      )}
      <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm">
        {/* Address (read-only) */}
        <div className="mb-1">
          <span className="font-medium text-neutral-500">To: </span>
          <span className="text-neutral-700">{resource.address}</span>
          <CopyButton text={resource.address} label="Copy" className="ml-2" />
        </div>
        {/* Subject (editable) */}
        <div className="mb-2">
          <span className="font-medium text-neutral-500">Subject: </span>
          <AutoResizeTextarea
            value={subject}
            onChange={setSubject}
            className="inline text-neutral-700 caret-primary-600"
          />
        </div>
        {/* Body (editable, with fake cursor) */}
        <div className="border-t border-neutral-200 pt-2">
          <AutoResizeTextarea
            value={body}
            onChange={setBody}
            className="whitespace-pre-wrap text-neutral-700 caret-primary-600"
            showFakeCursor
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <SaveButton
          isDirty={isDirty}
          isSaving={updateResources.isPending}
          saveError={saveError}
          onSave={handleSave}
        />
        <CopyButton text={fullText} label="Copy All" />
      </div>
    </div>
  );

  if (embedded) {
    return content;
  }

  return <Card>{content}</Card>;
}

/**
 * Editable text resource with inline editing for content.
 *
 * When `embedded` is true, renders without the Card wrapper and header
 * (label/icon), since those are provided by the parent ContentDraftItem.
 */
export function EditableTextResource({
  resource,
  resourceIndex,
  taskId,
  allResources,
  embedded = false,
}: EditableTextResourceProps) {
  const [content, setContent] = useState(resource.content);
  const [saveError, setSaveError] = useState(false);

  const updateResources = useUpdateTaskResources();

  const isDirty = content !== resource.content;

  const handleSave = useCallback(() => {
    setSaveError(false);
    const updatedResources = allResources.map((r, i) => {
      if (i === resourceIndex) {
        return { ...r, content } as TaskResource;
      }
      return r;
    });

    updateResources.mutate(
      { taskId, data: { resources: updatedResources } },
      {
        onError: () => setSaveError(true),
      },
    );
  }, [taskId, resourceIndex, allResources, content, updateResources]);

  // Sync local state if the resource prop changes (e.g., after server refetch)
  useEffect(() => {
    if (!isDirty) {
      setContent(resource.content);
    }
  }, [resource.content]);

  const innerContent = (
    <div className="space-y-2">
      {!embedded && (
        <div className="flex items-center gap-2">
          <svg className="h-5 w-5 flex-shrink-0 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-sm font-medium text-neutral-900">{resource.label}</span>
          {isDirty && (
            <span className="text-xs text-neutral-500 italic">Unsaved changes</span>
          )}
        </div>
      )}
      {embedded && isDirty && (
        <span className="text-xs text-neutral-500 italic">Unsaved changes</span>
      )}
      <div className="rounded-lg border border-primary-200 bg-primary-50 p-3">
        <AutoResizeTextarea
          value={content}
          onChange={setContent}
          className="whitespace-pre-wrap text-sm text-neutral-700 caret-primary-600"
          showFakeCursor
        />
      </div>
      <div className="flex items-center gap-2">
        <SaveButton
          isDirty={isDirty}
          isSaving={updateResources.isPending}
          saveError={saveError}
          onSave={handleSave}
        />
        <CopyButton text={content} label="Copy" />
      </div>
    </div>
  );

  if (embedded) {
    return innerContent;
  }

  return <Card>{innerContent}</Card>;
}
